import { Model, type ModelProps } from '@/infra/model'
import type { DocumentType } from './DocumentType'
import { Id } from '@/infra/id'
import { action, computed, observable } from 'mobx'
import { unixEpoch } from '@/utils/date-util'
import { collection } from '@/infra/collection'
import { DocumentAccess, type DocumentAccessProps } from './DocumentAccess'
import { isNotNil } from '@/utils/object-util'
import { match } from '@/utils/pattern-match-util'

export interface DocumentProps extends Omit<ModelProps<Document>, 'accesses'> {
  /**
   * The type name, if available.
   */
  typeName?: string

  /**
   * Document accesses, if any.
   */
  accesses?: DocumentAccessProps[]
}

/**
 * An uploaded document.
 */
export class Document extends Model<Document> {
  @observable private accessor _typeName: string | null = null
  @observable private accessor _typeImageUrl: string | null = null

  id = Id.none
  longId = Id.none

  @observable accessor title = ''
  @observable accessor dateCreated = unixEpoch()
  @observable accessor dateModified = unixEpoch()
  @observable accessor file: DocumentFile | null = null
  @observable accessor authorUserId = Id.none
  @observable accessor ownerUserId = Id.none
  @observable accessor purged = false
  @observable accessor latestVersionId = Id.none
  @observable accessor workspaceId = Id.none

  /**
   * Accesses for this document.
   */
  readonly accesses = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new DocumentAccess(),
  })

  /**
   * ID of the type.
   *
   * @remarks This is the short ID.
   */
  @observable accessor typeId = Id.none

  constructor(private readonly _getType: (id: Id) => DocumentType | null) {
    super()
  }

  /**
   * Getter for the document type.
   * @private
   */
  @computed
  private get _documentType() {
    return this._getType(this.typeId)
  }

  /**
   * Gets the type name from the document type if available,
   * falls back to the type name that was observed when loading.
   */
  @computed get typeName() {
    return this._documentType?.name ?? this._typeName
  }

  /**
   * Gets the type image URL from the document type if available.
   */
  @computed get typeImageUrl() {
    return this._documentType?.imageUrl ?? this._typeImageUrl
  }

  /**
   * Whether the document has been uploaded entirely. This does not necessarily
   * imply that the document is downloadable yet, though.
   */
  @computed get isFullyUploaded() {
    return Boolean(this.file)
  }

  /**
   * Whether the file is currently being processed.
   */
  @computed get isProcessing() {
    return Boolean(
      this.file?.state === 'Pending' || this.file?.state === 'Scanning',
    )
  }

  /**
   * Computed property for job IDs derived from accesses.
   */
  @computed get jobIds(): Id[] {
    return this.accesses.items
      .map((access) =>
        match(access.accessType, 'type', {
          Job: (jobAccess) => jobAccess.jobId,
          Unknown: () => null,
        }),
      )
      .filter(isNotNil)
  }

  /**
   * Whether the document is linked to the specified job.
   *
   * @param jobId
   */
  isLinkedToJob(jobId: Id): boolean {
    if (!jobId) {
      return false
    }

    return this.jobIds.includes(Id.assert(jobId))
  }

  /**
   * Sets the props on the model.
   */
  @action.bound
  override set({
    typeName,
    file,
    accesses,
    ...props
  }: Partial<DocumentProps>): this {
    if (typeName) {
      this._typeName = typeName
    }

    if (file && this.file) {
      // Don't replace the file if the one we're receiving has a status that is
      // considered earlier than the one we have.
      const isDowngrade =
        file.state === 'Pending' && this.file.state !== 'Pending'
      if (!isDowngrade) {
        this.file = file
      }
    } else {
      this.file = file ?? null
    }

    if (accesses) {
      this.accesses.reset(accesses)
    }

    return super.set(props)
  }
}

/**
 * What state the file is in.
 */
export type DocumentFileState =
  | 'Downloadable'
  | 'Purged'
  | 'Scanning'
  | 'Pending'
  | 'Failed'
  | 'VerifyingClientData'
  | 'ManualVerificationRequired'

/**
 * The underlying file of the document.
 */
export interface DocumentFile {
  readonly id: string
  readonly fileName: string
  readonly dateCreated: Date
  readonly state: DocumentFileState
}
